import React, { useState } from "react";

interface Partner {
  id: string;
  org: string;
  type: string;
  contact: string;
  details: string;
  lastContact: string;
  nextReview: string;
  notes: string;
}

interface Innovation {
  id: string;
  title: string;
  description: string;
  date: string;
  impact: string;
  status: string;
}

interface Store {
  partners: Partner[];
  innovations: Innovation[];
}

const KEY = "partnerships_records";
const TYPES = ["NHS / ICB", "GP Practice", "Pharmacy", "Dentistry", "Optometry", "Podiatry", "SALT", "Occupational Therapy", "Physiotherapy", "Dietetics", "Mental Health Team", "Hospice / Palliative", "College / Training", "Research / University", "Community / Voluntary", "Other"];

function load(): Store {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as Store;
  } catch {}
  return { partners: [], innovations: [] };
}

export default function Partnerships({ user }: { user?: any }) {
  const [store, setStore] = useState<Store>(load);
  const [partnerForm, setPartnerForm] = useState<Partial<Partner>>({ type: "NHS / ICB" });
  const [innoForm, setInnoForm] = useState<Partial<Innovation>>({ status: "Live" });
  const [message, setMessage] = useState("");

  const save = (next: Store) => {
    setStore(next);
    localStorage.setItem(KEY, JSON.stringify(next));
  };

  const addPartner = () => {
    if (!partnerForm.org) { setMessage("Enter the organisation name."); return; }
    save({ ...store, partners: [{ id: `pt-${Date.now()}`, org: partnerForm.org || "", type: partnerForm.type || "Other", contact: partnerForm.contact || "", details: partnerForm.details || "", lastContact: partnerForm.lastContact || "", nextReview: partnerForm.nextReview || "", notes: partnerForm.notes || "" }, ...store.partners] });
    setPartnerForm({ type: "NHS / ICB" });
    setMessage("Partnership added.");
  };

  const addInnovation = () => {
    if (!innoForm.title) { setMessage("Enter the innovation title."); return; }
    save({ ...store, innovations: [{ id: `in-${Date.now()}`, title: innoForm.title || "", description: innoForm.description || "", date: innoForm.date || new Date().toISOString().slice(0, 10), impact: innoForm.impact || "", status: innoForm.status || "Live" }, ...store.innovations] });
    setInnoForm({ status: "Live" });
    setMessage("Innovation registered.");
  };

  const removePartner = (id: string) => save({ ...store, partners: store.partners.filter(p => p.id !== id) });
  const removeInnovation = (id: string) => save({ ...store, innovations: store.innovations.filter(i => i.id !== id) });

  const daysTo = (d: string) => Math.ceil((new Date(d).getTime() - Date.now()) / 86400000);

  const printRegister = () => {
    const partners = store.partners.map(p => `<tr><td style="border-bottom:1px solid #eee;padding:6px">${p.org}</td><td style="border-bottom:1px solid #eee;padding:6px">${p.type}</td><td style="border-bottom:1px solid #eee;padding:6px">${p.contact || "-"}</td><td style="border-bottom:1px solid #eee;padding:6px">${p.lastContact || "-"}</td><td style="border-bottom:1px solid #eee;padding:6px">${p.notes || "-"}</td></tr>`).join("");
    const inno = store.innovations.map(i => `<tr><td style="border-bottom:1px solid #eee;padding:6px">${i.title}</td><td style="border-bottom:1px solid #eee;padding:6px">${i.date}</td><td style="border-bottom:1px solid #eee;padding:6px">${i.status}</td><td style="border-bottom:1px solid #eee;padding:6px">${i.impact || "-"}</td></tr>`).join("");
    const html = `<!DOCTYPE html><html><head><title>Partnerships & Innovation</title><style>body{font-family:Arial;margin:40px;color:#1f2937}h2,h3{color:#123B36}table{width:100%;border-collapse:collapse;font-size:13px}</style></head><body><h2>Partnerships & Innovation Register - Roop Cottage Residential Care Home</h2><h3>Community & clinical partnerships</h3><table><tr><th>Organisation</th><th>Type</th><th>Contact</th><th>Last contact</th><th>What it delivers</th></tr>${partners}</table><h3>Innovations</h3><table><tr><th>Innovation</th><th>Date</th><th>Status</th><th>Impact</th></tr>${inno}</table></body></html>`;
    const win = window.open("", "_blank");
    if (win) { win.document.write(html); win.document.close(); win.focus(); win.print(); }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Partnerships & Innovation Register</h2>
          <p className="text-sm text-slate-600">Strong community and NHS partnerships plus registered innovation - both are Outstanding markers.</p>
        </div>
        <button onClick={printRegister} className="rounded-lg bg-red-700 px-4 py-2 text-sm font-medium text-white">Print register</button>
      </div>

      {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 font-semibold text-slate-900">Add a partnership</h3>
        <div className="grid gap-3 md:grid-cols-3">
          <input value={partnerForm.org || ""} onChange={e => setPartnerForm(f => ({ ...f, org: e.target.value }))} placeholder="Organisation (e.g. Mid Yorkshire ICB)" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          <select value={partnerForm.type || "NHS / ICB"} onChange={e => setPartnerForm(f => ({ ...f, type: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm">
            {TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
          <input value={partnerForm.contact || ""} onChange={e => setPartnerForm(f => ({ ...f, contact: e.target.value }))} placeholder="Contact person / details" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          <input type="date" value={partnerForm.lastContact || ""} onChange={e => setPartnerForm(f => ({ ...f, lastContact: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          <input type="date" value={partnerForm.nextReview || ""} onChange={e => setPartnerForm(f => ({ ...f, nextReview: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          <input value={partnerForm.notes || ""} onChange={e => setPartnerForm(f => ({ ...f, notes: e.target.value }))} placeholder="What the partnership delivers" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
        </div>
        <button onClick={addPartner} className="mt-3 rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Add partnership</button>
      </div>

      <div className="space-y-2">
        {store.partners.map(p => (
          <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm font-medium text-slate-900">{p.org} <span className="text-xs text-slate-500">{p.type} | {p.contact}</span></div>
              <div className="flex items-center gap-2">
                {p.nextReview ? <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${daysTo(p.nextReview) < 0 ? "bg-red-100 text-red-700" : daysTo(p.nextReview) <= 30 ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>{daysTo(p.nextReview) < 0 ? "Review overdue" : `Review in ${daysTo(p.nextReview)}d`}</span> : null}
                <button onClick={() => removePartner(p.id)} className="text-xs text-red-600">Remove</button>
              </div>
            </div>
            <div className="mt-1 text-xs text-slate-600">{p.notes || "-"} {p.lastContact ? `| Last contact ${p.lastContact}` : ""}</div>
          </div>
        ))}
        {store.partners.length === 0 ? <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500">No partnerships recorded yet.</div> : null}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 font-semibold text-slate-900">Register an innovation</h3>
        <div className="grid gap-3 md:grid-cols-2">
          <input value={innoForm.title || ""} onChange={e => setInnoForm(f => ({ ...f, title: e.target.value }))} placeholder="Title (e.g. RMACS AI Intelligence Engine)" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          <input type="date" value={innoForm.date || ""} onChange={e => setInnoForm(f => ({ ...f, date: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          <textarea value={innoForm.description || ""} onChange={e => setInnoForm(f => ({ ...f, description: e.target.value }))} rows={2} placeholder="What it is and how it improves care" className="rounded-lg border border-slate-300 px-2 py-1 text-sm md:col-span-2" />
          <input value={innoForm.impact || ""} onChange={e => setInnoForm(f => ({ ...f, impact: e.target.value }))} placeholder="Measured impact / outcome" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          <select value={innoForm.status || "Live"} onChange={e => setInnoForm(f => ({ ...f, status: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm">
            <option>Live</option><option>Pilot</option><option>Planned</option>
          </select>
        </div>
        <button onClick={addInnovation} className="mt-3 rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Register innovation</button>
      </div>

      <div className="space-y-2">
        {store.innovations.map(i => (
          <div key={i.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm font-medium text-slate-900">{i.title} <span className="text-xs text-slate-500">{i.date} | {i.status}</span></div>
              <button onClick={() => removeInnovation(i.id)} className="text-xs text-red-600">Remove</button>
            </div>
            <div className="mt-1 text-xs text-slate-600">{i.description} {i.impact ? `| Impact: ${i.impact}` : ""}</div>
          </div>
        ))}
        {store.innovations.length === 0 ? <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500">No innovations registered yet. Your AI intelligence engine and this system itself count - register them.</div> : null}
      </div>
    </div>
  );
}